import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Injector,
	OnDestroy,
	OnInit,
	effect,
	inject
} from '@angular/core';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryService } from '@go-modules/services/library/library.service';
import { RubricTemplate } from '@go-modules/interfaces/rubrics/rubric';
import { Subject, finalize, take, takeUntil } from 'rxjs';
import { rubricEditorModalToken } from 'go-modules/modals/rubric-editor/modal.factory';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { Activity } from '@go-modules/interfaces/activity';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { noop } from 'angular';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';
import { DisabledInteractiveDirective } from '@go-modules/directives/disabled-interactive/disabled-interactive.directive';
import { ParticipantRubricSelection } from './participant-rubric-setting.model';
import type { LibraryDialogContext } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';

const PARTICIPANT_RUBRIC_ROLE = 'student';
type ViewableActivity = Activity & Pick<ActivityModel, 'isViewable'>;

@Component({
	selector: 'participant-rubric-setting',
	templateUrl: './participant-rubric-setting.component.html',
	styleUrls: ['./participant-rubric-setting.component.scss'],
	standalone: true,
	imports: [
		TranslateModule,
		MatTooltipModule,
		PanelModule,
		NgxToolbarModule,
		NgxToolbarButtonModule,
		DisabledInteractiveDirective
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParticipantRubricSettingComponent implements OnInit, OnDestroy {
	public selectedParticipantRubric: ParticipantRubricSelection | null = null;
	public toolbarActions = Action;
	public isParticipantRubricLoading = false;

	private readonly destroy$ = new Subject<void>();
	private isDestroyed = false;
	private participantRubricLoadRequestId = 0;
	private loadedParticipantRubricId: number | null = null;
	private readonly injector = inject(Injector);
	private readonly goModal = inject(GoModalService);
	private readonly libraryService = inject(LibraryService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly matDialog = inject(MatDialog);
	private readonly translate = inject(TranslateService);
	private readonly rubricEditor = inject(rubricEditorModalToken);
	private readonly activityModelFactory = inject(activityToken) as {
		model(data?: unknown, skipCache?: boolean): ActivityModel;
	};
	private readonly editorContext: ActivityEditorContext = inject(ACTIVITY_EDITOR_CONTEXT);

	public ngOnInit (): void {
		effect(() => {
			this.syncRubricFromActivity(this.editorContext.activity());
		}, { injector: this.injector });
	}

	public ngOnDestroy (): void {
		this.isDestroyed = true;
		this.participantRubricLoadRequestId++;
		this.destroy$.next();
		this.destroy$.complete();
	}

	public shouldShowParticipantRubric (): boolean {
		const activity = this.editorContext.activity();
		if (activity == null) {
			return true;
		}

		if (this.isActivityModel(activity)) {
			return activity.isViewable('peer_rubric_id');
		}

		return this.activityModelFactory.model(activity, true).isViewable('peer_rubric_id');
	}

	public openRubricSelector (): void {
		if (this.isRubricSelectionUnavailable()) {
			return;
		}

		void import('@go-modules/components/dialogs/library-dialog/library-dialog.component')
			.then(({ LibraryDialogComponent }) => this.goModal.open(LibraryDialogComponent, false, {
				data: {
					filterType: [CONTENT_TYPES.RUBRICS],
					mode: MODES.SELECT,
					collectionManagement: true,
					context: this.getLibraryDialogContext()
				}
			}).afterClosed()
				.pipe(takeUntil(this.destroy$))
				.subscribe((rubric: RubricTemplate | null) => {
					if (rubric?.id) {
						this.setSelectedParticipantRubric(rubric);
					}
				}));
	}

	public isRubricSelectionUnavailable (): boolean {
		return this.editorContext.group?.() == null && this.editorContext.account?.() == null;
	}

	public removeParticipantRubric (): void {
		const selectedParticipantRubric = this.getAvailableParticipantRubric();
		if (selectedParticipantRubric == null) {
			return;
		}

		this.libraryService.checkRubricUsage(selectedParticipantRubric.id, PARTICIPANT_RUBRIC_ROLE)
			.pipe(takeUntil(this.destroy$), take(1))
			.subscribe((response) => {
				if (!this.isCurrentParticipantRubricSelection(selectedParticipantRubric.id)) {
					return;
				}

				const activity = this.editorContext.activity();
				if (response.in_use && activity != null && response.activities.includes(activity.activity_id)) {
					this.showMessageDialog(
						'modal-rubric-delete_title',
						'modal-rubric-delete_message'
					);
				} else {
					this.setSelectedParticipantRubric(null);
				}
			});
	}

	public previewParticipantRubric (): void {
		const selectedParticipantRubric = this.getAvailableParticipantRubric();
		if (selectedParticipantRubric == null) {
			return;
		}

		this.rubricEditor.open({
			modalData: {
				options: {
					mode: 'live',
					previewOnly: true,
					postData: {
						group_id: this.editorContext.activity()?.group_id
					},
					schemaId: selectedParticipantRubric.id
				}
			}
		});
	}

	public editParticipantRubric (): void {
		const selectedParticipantRubric = this.getAvailableParticipantRubric();
		if (selectedParticipantRubric == null) {
			return;
		}

		const currentActivity = this.editorContext.activity();
		if (currentActivity == null) {
			return;
		}

		this.libraryService.checkRubricUsage(selectedParticipantRubric.id, PARTICIPANT_RUBRIC_ROLE)
			.pipe(takeUntil(this.destroy$), take(1))
			.subscribe((response) => {
				if (!this.isCurrentParticipantRubricSelection(selectedParticipantRubric.id)) {
					return;
				}

				if (response.in_use) {
					if (response.activities.includes(currentActivity.activity_id)) {
						this.showMessageDialog(
							'modal-rubric-edit_title',
							'modal-rubric-edit_message'
						);
					} else {
						this.openCopyParticipantRubricModal(true, currentActivity);
					}
					return;
				}

				if (!response.can_edit) {
					this.openCopyParticipantRubricModal(
						false,
						currentActivity
					);
					return;
				}

				this.openParticipantRubricEditor(
					currentActivity,
					selectedParticipantRubric.id
				);
			});
	}

	private getLibraryDialogContext (): LibraryDialogContext {
		return {
			group: this.editorContext.group?.() ?? null,
			account: this.editorContext.account?.() ?? null,
			org: this.editorContext.org?.() ?? null,
			license: this.editorContext.license?.() ?? null,
			orgSettings: this.editorContext.orgSettings?.() ?? null
		};
	}

	private syncRubricFromActivity (activity: Activity | null): void {
		const rubricId = activity?.peer_rubric_id ?? null;
		if (rubricId === this.loadedParticipantRubricId) {
			return;
		}

		this.loadedParticipantRubricId = rubricId;
		if (rubricId) {
			this.loadParticipantRubric(rubricId);
		} else {
			this.cancelParticipantRubricLoad();
			this.selectedParticipantRubric = null;
			this.cdr.markForCheck();
		}
	}

	private loadParticipantRubric (rubricId: number): void {
		const requestId = ++this.participantRubricLoadRequestId;
		this.isParticipantRubricLoading = true;
		this.libraryService.getRubricTemplate(rubricId)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					if (!this.isCurrentParticipantRubricLoad(requestId)) {
						return;
					}

					this.isParticipantRubricLoading = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe({
				next: (rubric) => {
					if (!this.isCurrentParticipantRubricLoad(requestId)) {
						return;
					}

					this.selectedParticipantRubric = {
						id: rubric.id,
						title: rubric.title ?? ''
					};
				},
				error: () => {
					if (!this.isCurrentParticipantRubricLoad(requestId)) {
						return;
					}

					this.selectedParticipantRubric = null;
				}
			});
	}

	private cancelParticipantRubricLoad (): void {
		this.participantRubricLoadRequestId++;
		this.isParticipantRubricLoading = false;
		this.cdr.markForCheck();
	}

	private setSelectedParticipantRubric (rubric: RubricTemplate | null): void {
		const currentActivity = this.editorContext.activity();
		if (currentActivity == null) {
			return;
		}

		if (rubric) {
			this.selectedParticipantRubric = {
				id: rubric.id,
				title: rubric.title ?? ''
			};
			this.loadedParticipantRubricId = rubric.id;
			this.applyActivityChanges({
				peer_rubric_id: rubric.id,
				peer_rubric_critique_enabled: !!currentActivity.peer_review,
				self_rubric_critique_enabled: true
			});
		} else {
			this.selectedParticipantRubric = null;
			this.loadedParticipantRubricId = null;
			this.applyActivityChanges({
				peer_rubric_id: null,
				peer_rubric_critique_enabled: false,
				self_rubric_critique_enabled: false
			});
		}

		this.cdr.markForCheck();
	}

	private applyActivityChanges (changes: Partial<Activity>): void {
		const currentActivity = this.editorContext.activity();
		if (currentActivity == null) {
			return;
		}

		this.editorContext.updateActivity({
			...currentActivity,
			...changes
		} as Activity);
	}

	private openParticipantRubricEditor (activity: Activity, schemaId: number): void {
		if (!this.rubricEditor) {
			return;
		}

		this.rubricEditor.open({
			modalData: {
				options: {
					mode: 'edit',
					previewOnly: false,
					postData: {
						group_id: activity.group_id
					},
					schemaId
				}
			}
		}).result?.then((template: RubricTemplate) => {
			if (this.isDestroyed || !this.isCurrentParticipantRubricSelection(schemaId)) {
				return;
			}

			if (template) {
				this.setSelectedParticipantRubric(template);
			}
		}).catch(noop);
	}

	private openCopyParticipantRubricModal (isInUse: boolean, activity: Activity): void {
		const selected = this.selectedParticipantRubric;
		if (selected == null) {
			return;
		}

		ConfirmDialogComponent.open(this.matDialog, {
			data: {
				title: this.translate.instant('modal-rubric-edit-copy_title'),
				message: isInUse
					? this.translate.instant('modal-rubric-edit-decouple_message')
					: this.translate.instant('modal-rubric-edit-no-permission_message'),
				confirmText: this.translate.instant('common_create-copy'),
				cancelText: this.translate.instant('common_cancel')
			}
		}).afterClosed()
			.pipe(takeUntil(this.destroy$), take(1))
			.subscribe((confirm: boolean) => {
				if (!confirm) {
					return;
				}

				if (this.getAvailableParticipantRubric()?.id !== selected.id) {
					return;
				}

				this.libraryService.copyRubricTemplate(selected.id)
					.pipe(takeUntil(this.destroy$), take(1))
					.subscribe((rubric) => {
						if (this.getAvailableParticipantRubric()?.id !== selected.id) {
							return;
						}

						this.setSelectedParticipantRubric(rubric);
						this.openParticipantRubricEditor(activity, rubric.id);
					});
			});
	}

	private showMessageDialog (titleKey: string, messageKey: string): void {
		MessageDialogComponent.open(this.matDialog, {
			data: {
				title: this.translate.instant(titleKey),
				message: this.translate.instant(messageKey)
			}
		});
	}

	private getAvailableParticipantRubric (): ParticipantRubricSelection | null {
		if (this.isParticipantRubricLoading) {
			return null;
		}

		return this.selectedParticipantRubric;
	}

	private isCurrentParticipantRubricLoad (requestId: number): boolean {
		return !this.isDestroyed && this.participantRubricLoadRequestId === requestId;
	}

	private isCurrentParticipantRubricSelection (rubricId: number): boolean {
		return this.getAvailableParticipantRubric()?.id === rubricId;
	}

	private isActivityModel (activity: Activity): activity is ViewableActivity {
		return (activity as Partial<ViewableActivity>).isViewable != null;
	}
}
